/* ==========================================================================
   dtc-ui.js. DesignTeesCentral shared runtime
   Vanilla JS. No libraries. Loaded with `defer` from layout/theme.liquid.

   Public API (window.DTC):
     DTC.reducedMotion            -> boolean (live-updating)
     DTC.register(name, {init, destroy})
     DTC.toast(message)
     DTC.spy(sections, onChange)  -> {disconnect()}
     DTC.setCartCount(n) / DTC.refreshCart()
     DTC.on(el, type, fn, opts)   -> off()  (tiny helper returning a remover)
     DTC.scrollToEl(el)
     DTC.money(cents)
   ========================================================================== */
(function () {
  'use strict';

  var DTC = window.DTC || {};
  window.DTC = DTC;

  /* ------------------------------------------------------------------ *
   * (e) Reduced-motion flag, read once, then kept in sync.
   * ------------------------------------------------------------------ */
  var rmQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  DTC.reducedMotion = rmQuery.matches;
  var onRm = function (e) { DTC.reducedMotion = e.matches; };
  if (rmQuery.addEventListener) rmQuery.addEventListener('change', onRm);
  else if (rmQuery.addListener) rmQuery.addListener(onRm);

  /* ------------------------------------------------------------------ *
   * Tiny event helper. Returns an "off" function so section destroy()
   * handlers stay one-liners.
   * ------------------------------------------------------------------ */
  DTC.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    return function off() { el.removeEventListener(type, fn, opts); };
  };

  /* ------------------------------------------------------------------ *
   * (a) SECTION LIFECYCLE
   *
   * A section registers once at parse time:
   *
   *   DTC.register('turntable', {
   *     init: function (root) { ...; return function cleanup() {...}; },
   *     destroy: function (root) {}      // optional
   *   });
   *
   * `root` is the element carrying data-dtc-section="<name>".
   * Every matching root on the page is initialised on DOMContentLoaded,
   * and re-initialised on shopify:section:load (theme editor). Instances
   * are torn down on shopify:section:unload / :select-away.
   * `init` may return a cleanup function, it runs before destroy().
   * ------------------------------------------------------------------ */
  var registry = Object.create(null);   // name -> {init, destroy}
  var instances = new WeakMap();        // root element -> cleanup fn

  function rootsIn(scope, name) {
    var sel = name
      ? '[data-dtc-section="' + name + '"]'
      : '[data-dtc-section]';
    var found = [];
    if (scope.matches && scope.matches(sel)) found.push(scope);
    if (scope.querySelectorAll) {
      Array.prototype.push.apply(found, scope.querySelectorAll(sel));
    }
    return found;
  }

  function mount(root) {
    if (instances.has(root)) return;            // already live
    var def = registry[root.getAttribute('data-dtc-section')];
    if (!def || typeof def.init !== 'function') return;
    var cleanup;
    try { cleanup = def.init(root); }
    catch (err) { console.error('[DTC] init failed', root, err); return; }
    instances.set(root, typeof cleanup === 'function' ? cleanup : null);
  }

  function unmount(root) {
    if (!instances.has(root)) return;
    var cleanup = instances.get(root);
    instances.delete(root);
    try { if (cleanup) cleanup(); } catch (err) { console.error('[DTC] cleanup failed', err); }
    var def = registry[root.getAttribute('data-dtc-section')];
    if (def && typeof def.destroy === 'function') {
      try { def.destroy(root); } catch (err) { console.error('[DTC] destroy failed', err); }
    }
  }

  DTC.register = function (name, def) {
    registry[name] = def;
    // If the DOM is already parsed (e.g. a section script that loads late),
    // mount immediately.
    if (document.readyState !== 'loading') rootsIn(document, name).forEach(mount);
  };

  DTC.mountAll = function (scope) { rootsIn(scope || document, null).forEach(mount); };
  DTC.unmountAll = function (scope) { rootsIn(scope || document, null).forEach(unmount); };

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  ready(function () { DTC.mountAll(document); });

  // Theme editor compatibility.
  document.addEventListener('shopify:section:load', function (e) { DTC.mountAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { DTC.unmountAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { DTC.mountAll(e.target); });
  document.addEventListener('shopify:section:deselect', function (e) {
    // Keep mounted; sections stay interactive while deselected.
    void e;
  });

  /* ------------------------------------------------------------------ *
   * (b) SCROLL SPY (IntersectionObserver), powers the star nav.
   *
   *   DTC.spy(nodeListOfSections, function (activeEl, index) {...})
   *
   * "Active" = the last section whose top has crossed 45% of the viewport,
   * matching the design prototype. IntersectionObserver with a set of
   * rootMargins gives us that without a scroll handler per frame.
   * ------------------------------------------------------------------ */
  DTC.spy = function (sections, onChange) {
    var list = Array.prototype.slice.call(sections);
    if (!list.length) return { disconnect: function () {} };

    var current = -1;

    function evaluate() {
      var vh = window.innerHeight;
      var next = -1;
      for (var i = 0; i < list.length; i++) {
        if (list[i].getBoundingClientRect().top < vh * 0.45) next = i;
      }
      if (next !== current) {
        current = next;
        onChange(next < 0 ? null : list[next], next);
      }
    }

    // The observer wakes us up; a rAF-throttled scroll listener does the
    // precise 45% math (cheap: 5 getBoundingClientRect calls).
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; evaluate(); });
    }

    var io = new IntersectionObserver(onScroll, {
      rootMargin: '-45% 0px -55% 0px',
      threshold: 0
    });
    list.forEach(function (el) { io.observe(el); });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate();

    return {
      disconnect: function () {
        io.disconnect();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    };
  };

  /* ------------------------------------------------------------------ *
   * (c) TOAST, ink pill, bottom-center, 2.6s.
   * Renders into the #dtc-toast container from snippets/dtc-toast.liquid.
   * ------------------------------------------------------------------ */
  var toastTimer = null;
  DTC.toast = function (message) {
    var el = document.getElementById('dtc-toast');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 2600);
  };

  /* ------------------------------------------------------------------ *
   * (d) CART COUNT
   *
   * Any element with [data-cart-count] gets its text set to the number.
   * Any element with [data-cart-count-text] gets a full label, using the
   * template in its data-template attribute ("CART ({{ count }})").
   * ------------------------------------------------------------------ */
  DTC.setCartCount = function (count) {
    var n = Number(count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = String(n);
    });
    document.querySelectorAll('[data-cart-count-text]').forEach(function (el) {
      var tpl = el.getAttribute('data-template') || 'CART ({{ count }})';
      el.textContent = tpl.replace('{{ count }}', String(n));
    });
    document.dispatchEvent(new CustomEvent('dtc:cart:updated', { detail: { count: n } }));
  };

  /** Fetch /cart.js and push the live item count into the header. */
  DTC.refreshCart = function () {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { DTC.setCartCount(cart.item_count); return cart; })
      .catch(function (err) { console.error('[DTC] cart refresh failed', err); });
  };

  /**
   * Add a variant to the cart and show a toast. Used by every quick-add
   * button in the theme.
   *   DTC.addToCart(variantId, 1, 'Sad Cat Tee')
   */
  DTC.addToCart = function (id, quantity, title) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: quantity || 1 }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message); });
        return r.json();
      })
      .then(function (res) {
        DTC.refreshCart();
        DTC.toast(title ? 'Added “' + title + '” to cart' : 'Added to cart');
        return res;
      })
      .catch(function (err) {
        DTC.toast(err.message || 'Sorry, that could not be added');
        throw err;
      });
  };

  /* ------------------------------------------------------------------ *
   * Delegated quick-add. Any <button data-dtc-quickadd data-variant-id="…"
   * data-product-title="…"> anywhere in the theme just works, including
   * markup injected later by the theme editor.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-dtc-quickadd]');
    if (!btn || btn.disabled) return;
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;
    e.preventDefault();
    btn.disabled = true;
    DTC.addToCart(id, 1, btn.getAttribute('data-product-title'))
      .catch(function () { /* toast already shown */ })
      .then(function () { btn.disabled = false; });
  });

  /* ------------------------------------------------------------------ *
   * Misc helpers
   * ------------------------------------------------------------------ */

  /** Smooth-scroll an element into view under the sticky header. */
  DTC.scrollToEl = function (el) {
    if (!el) return;
    var offset = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--scroll-offset'), 10
    ) || 66;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - offset,
      behavior: DTC.reducedMotion ? 'auto' : 'smooth'
    });
  };

  /** Format cents using the shop's money format (set in theme.liquid). */
  DTC.money = function (cents) {
    var fmt = DTC.moneyFormat || '${{amount}}';
    var amount = (Number(cents) / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, parts.join('.'));
  };

  /* ------------------------------------------------------------------ *
   * Image save-guard.
   *
   * WHAT THIS DOES: removes the three one-gesture ways to pull an image
   * off the page, right-click > Save image, drag-to-desktop, and the iOS
   * long-press sheet (that last one is CSS, in dtc-base.css).
   *
   * WHAT THIS DOES NOT DO: protect the artwork. The <img> src is a public
   * Shopify CDN URL sitting in the HTML. Devtools, view-source, curl, a
   * screenshot, or Google's image cache all still work, and none of them
   * touch this handler. Treat it as a speed bump for casual copying, and
   * never as a reason to publish art you couldn't afford to have taken.
   *
   * SCOPE: images only. Right-click stays live everywhere else, because
   * killing it page-wide takes "open link in new tab", "back", and
   * "search selection" away from honest customers to inconvenience no one.
   * Opt out of the guard with [data-allow-save]; opt a non-<img> in with
   * [data-no-save].
   * ------------------------------------------------------------------ */
  (function imageGuard() {
    /* The theme editor needs its normal context menus to stay usable. */
    var designMode = window.Shopify && window.Shopify.designMode;
    if (designMode) return;

    function guarded(target) {
      if (!target || target.nodeType !== 1) return false;
      if (typeof target.closest !== 'function') return false;
      if (target.closest('[data-allow-save]')) return false;
      return target.tagName === 'IMG' ||
             !!target.closest('picture') ||
             !!target.closest('[data-no-save]');
    }

    /* Right-click / long-press menu. */
    document.addEventListener('contextmenu', function (e) {
      if (guarded(e.target)) e.preventDefault();
    });

    /* Drag-to-desktop. The turntable, coverflow and special-collection
       stages already kill this on their own roots so their pointer drags
       stay clean; this covers every other image on the site. */
    document.addEventListener('dragstart', function (e) {
      if (guarded(e.target)) e.preventDefault();
    });
  }());

  /* ------------------------------------------------------------------ *
   * Global scroll state: adds .is-scrolled past 320px to <html> and to
   * anything with [data-scrolled-target]. Header wordmark + star nav both
   * key off this, so there is exactly one scroll listener for it.
   * ------------------------------------------------------------------ */
  (function scrolledFlag() {
    var THRESHOLD = 320;
    var state = null;
    var ticking = false;
    function apply() {
      var next = window.scrollY > THRESHOLD;
      if (next === state) return;
      state = next;
      document.documentElement.classList.toggle('dtc-scrolled', next);
      document.querySelectorAll('[data-scrolled-target]').forEach(function (el) {
        el.classList.toggle('is-scrolled', next);
      });
      document.dispatchEvent(new CustomEvent('dtc:scrolled', { detail: { scrolled: next } }));
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; apply(); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    ready(apply);
    // Re-apply when the editor injects new [data-scrolled-target] nodes.
    document.addEventListener('shopify:section:load', function () { state = null; apply(); });
  }());

  /* ------------------------------------------------------------------ *
   * Announcement marquee fitter.
   *
   * The bar renders its message run twice and scrolls translateX(-50%),
   * exactly one run width. That loop is only seamless when ONE run is at
   * least as wide as the viewport; with a handful of short messages the run
   * falls short and a blank gap crosses the bar once per cycle (the "not
   * continual" bug). Pure CSS can't know the run/viewport ratio, so here we
   * repeat each run's content until it comfortably exceeds the viewport, and
   * stretch the duration by the same factor so the scroll speed the merchant
   * set (px/second) is unchanged. Re-fits on resize and on editor reload.
   * Skipped under reduced motion, where the CSS collapses the bar to a
   * static, wrapping line and there is nothing to scroll.
   * ------------------------------------------------------------------ */
  (function marqueeFit() {
    function fit(track) {
      var runs = track.querySelectorAll('.dtc-announce__run');
      if (runs.length < 2 || !track.parentElement) return;
      // Cache the original one-copy unit + base duration on first sight, so
      // every re-fit rebuilds from the source, never from already-doubled text.
      var unit = track.__dtcUnit || (track.__dtcUnit = runs[0].innerHTML);
      var base = track.__dtcBase ||
        (track.__dtcBase = parseFloat(getComputedStyle(track).getPropertyValue('--t')) || 26);
      runs[0].innerHTML = unit;            // one copy, to measure it
      var unitW = runs[0].scrollWidth;
      if (unitW <= 0) return;              // bar hidden / not laid out yet
      // +1 copy of headroom so the run always overhangs the viewport edge.
      var reps = Math.max(1, Math.ceil(track.parentElement.clientWidth / unitW) + 1);
      var html = ''; for (var k = 0; k < reps; k++) html += unit;
      runs[0].innerHTML = html;
      runs[1].innerHTML = html;
      track.style.setProperty('--t', (base * reps) + 's');
    }
    function fitAll() {
      if (DTC.reducedMotion) return;
      document.querySelectorAll('.dtc-announce__track[data-anim="marquee"]').forEach(fit);
    }
    ready(fitAll);
    var deb;
    window.addEventListener('resize', function () {
      clearTimeout(deb); deb = setTimeout(fitAll, 200);
    }, { passive: true });
    document.addEventListener('shopify:section:load', fitAll);
  }());

  /* ==================================================================== *
   * RESTORED-DOCUMENT REVALIDATION, collection and search grids only
   *
   * Storefront HTML ships with no Cache-Control, no Expires and no
   * Last-Modified, so the response is storable and a back/forward
   * navigation or a restored tab can replay the body without ever making a
   * conditional request. Whatever availability was baked into that body is
   * what the shopper sees, however old it is: a shirt that was unavailable
   * for ten minutes stays "Sold out" in that one browser for days, which is
   * why the fault looked like it followed people rather than time.
   *
   * Product pages heal themselves in dtc-product.js by re-deriving
   * availability from the live product JSON. Grids carry no JavaScript, so
   * the only honest repair is a fresh document.
   *
   * This IS a blanket reload, by explicit decision. An earlier version only
   * reloaded when the restored grid already showed a sold-out badge, on the
   * reasoning that nothing else could cost a sale. That guard is gone: it
   * only repaired grids that were ALREADY wrong, and did nothing for the
   * opposite and more expensive error, a grid restored from before a shirt
   * came back in stock, or before a price, a title or a new arrival changed.
   * A restored grid is simply untrustworthy, so we do not try to guess which
   * parts of it went stale.
   *
   * THE COST IS REAL. Back on a collection or search page is now a full
   * network round trip instead of an instant bfcache restore. On a slow
   * mobile connection that is a visible pause on the single most-used
   * navigation in ecommerce. Product pages are deliberately excluded: they
   * re-derive availability from live JSON in dtc-product.js without a
   * reload, which is strictly better, so nothing is gained by nuking theirs.
   *
   * No reload loop: a reload is not a persisted restore, so `e.persisted`
   * is false on the way back in.
   * ==================================================================== */
  (function () {
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var tpl = document.body ? document.body.className : '';
      if (tpl.indexOf('template-collection') === -1 &&
          tpl.indexOf('template-search') === -1) return;
      window.location.reload();
    });
  }());
}());
